/**
 * EditableCell - Notion-style inline editing
 * Click to edit, blur to save, compact CRM styling
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface EditableCellProps {
  /** Current value to display */
  value: string | number | null | undefined;
  /** Callback when save is triggered on blur */
  onSave: (value: string) => Promise<void>;
  /** Callback when save fails */
  onError?: (error: Error) => void;
  /** Field type - affects input type */
  type?: 'text' | 'number' | 'email' | 'textarea' | 'select';
  /** Options for select type */
  options?: Array<{ value: string; label: string }>;
  /** Placeholder text */
  placeholder?: string;
  /** Whether editing is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Display formatter for read mode */
  formatter?: (value: string | number | null | undefined) => string;
  /** Minimum value for number type */
  min?: number;
  /** Maximum value for number type */
  max?: number;
  /** Maximum length for text/textarea */
  maxLength?: number;
  /** Number of rows for textarea */
  rows?: number;
}

export function EditableCell({
  value,
  onSave,
  onError,
  type = 'text',
  options,
  placeholder,
  disabled = false,
  className,
  formatter,
  min,
  max,
  maxLength,
  rows = 2,
}: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localValue, setLocalValue] = useState(String(value ?? ''));
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);
  const initialValue = useRef(String(value ?? ''));

  // Sync with external value changes
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(String(value ?? ''));
      initialValue.current = String(value ?? '');
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement || inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  const handleSave = useCallback(async () => {
    // No change - just exit edit mode
    if (localValue === initialValue.current) {
      setIsEditing(false);
      setError(null);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(localValue);
      initialValue.current = localValue;
      setIsEditing(false);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Save failed');
      setError(error.message);
      // Revert to original value on error
      setLocalValue(initialValue.current);
      onError?.(error);
    } finally {
      setIsSaving(false);
    }
  }, [localValue, onSave, onError]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && type !== 'textarea') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Revert and close on Escape
        setLocalValue(initialValue.current);
        setIsEditing(false);
        setError(null);
      }
    },
    [handleSave, type]
  );

  const handleBlur = useCallback(() => {
    handleSave();
  }, [handleSave]);

  const displayValue = formatter
    ? formatter(value)
    : String(value ?? '—');

  if (disabled) {
    return (
      <span className={cn('text-gray-500', className)}>
        {displayValue}
      </span>
    );
  }

  if (!isEditing) {
    return (
      <button
        type="button"
        onDoubleClick={() => setIsEditing(true)}
        className={cn(
          'text-left w-full px-1.5 py-0.5 -mx-1.5 -my-0.5 rounded',
          'hover:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-400',
          'transition-colors duration-100 text-xs',
          error && 'ring-1 ring-red-300',
          className
        )}
      >
        <span className={!value && value !== 0 ? 'text-gray-400 italic' : ''}>
          {displayValue || placeholder || '—'}
        </span>
      </button>
    );
  }

  const inputClasses = cn(
    'w-full px-1.5 py-0.5 border rounded text-xs',
    'focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400',
    error && 'border-red-300 ring-red-300',
    isSaving && 'opacity-50'
  );

  const renderInput = () => {
    if (type === 'textarea') {
      return (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={rows}
          className={inputClasses}
        />
      );
    }

    if (type === 'select' && options) {
      return (
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={localValue}
          onChange={async (e) => {
            const newValue = e.target.value;
            setLocalValue(newValue);

            // Save immediately with new value (not from stale closure)
            if (newValue === initialValue.current) {
              setIsEditing(false);
              return;
            }

            setIsSaving(true);
            setError(null);

            try {
              await onSave(newValue);
              initialValue.current = newValue;
              setIsEditing(false);
            } catch (err) {
              const error = err instanceof Error ? err : new Error('Save failed');
              setError(error.message);
              setLocalValue(initialValue.current);
              onError?.(error);
            } finally {
              setIsSaving(false);
            }
          }}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          className={inputClasses}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type={type}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        placeholder={placeholder}
        min={min}
        max={max}
        maxLength={maxLength}
        className={inputClasses}
      />
    );
  };

  return (
    <div className={cn('relative', className)}>
      <div className="flex items-center gap-0.5">
        {renderInput()}
        {isSaving && (
          <Loader2 className="h-3 w-3 animate-spin text-blue-500 flex-shrink-0" />
        )}
      </div>
      {error && (
        <p className="absolute left-0 -bottom-4 text-[10px] text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
